import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import axios from 'axios';
import webservice from '../../../services/webservice';
import swal from 'sweetalert2';
import './admin.css';
import { AdminMenuList } from '../../layouts/nav/adminMenu';

const AddAdmin = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditMode = Boolean(id);

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        mobile: '',
        userType: '',
        password: '',
        confirmPassword: ''
    });
    const [roles, setRoles] = useState([]);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await axios.get(`${webservice}api/role-list`, { 
                    params: { limit: 1000, status: 'Active' } 
                });
                if (response.data.success) {
                    setRoles(response.data.data);
                }
            } catch (error) {
                console.error('Error fetching roles:', error);
            }
        };
        fetchRoles();

        if (isEditMode) {
            const fetchAdmin = async () => {
                try {
                    const response = await axios.get(`${webservice}api/admin/${id}`);
                    if (response.data.success) {
                        const admin = response.data.data;
                        const [firstName, ...lastNameParts] = admin.name.split(' ');
                        const lastName = lastNameParts.join(' ');
                        setFormData({
                            firstName: firstName || '',
                            lastName: lastName || '',
                            mobile: admin.mobile || '',
                            userType: admin.userType || '',
                            password: '', // leave empty for edit
                            confirmPassword: ''
                        });
                    }
                } catch (error) {
                    console.error('Error fetching admin details:', error);
                }
            };
            fetchAdmin();
        }
    }, [id, isEditMode]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'mobile') {
            // Allow only numbers
            const sanitizedValue = value.replace(/[^0-9]/g, '');
            setFormData({ ...formData, [name]: sanitizedValue });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!isEditMode) {
            if (formData.password !== formData.confirmPassword) {
                setError("Passwords do not match!");
                return;
            }

            if (formData.password.length < 6) {
                setError("Password must be at least 6 characters long.");
                return;
            }
        }

        if (formData.mobile && formData.mobile.length !== 10) {
            swal.fire({
                title: 'Notice',
                text: 'Phone number must be exactly 10 digits',
                icon: 'warning',
                confirmButtonText: 'Ok'
            });
            return;
        }

        const name = `${formData.firstName} ${formData.lastName}`.trim();
        const dataToSubmit = {
            name,
            mobile: formData.mobile,
            userType: formData.userType
        };

        if (!isEditMode && formData.password) {
            dataToSubmit.password = formData.password;
        }

        if (isEditMode) {
            dataToSubmit.id = id;
        }

        try {
            const endpoint = isEditMode ? 'api/update-admin-details' : 'api/create-admin';
            const response = await axios.post(`${webservice}${endpoint}`, dataToSubmit);
            if (response.data.success) {
                swal.fire({
                    title: 'Success',
                    text: `Admin account ${isEditMode ? 'updated' : 'created'} successfully!`,
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                }).then(() => {
                    navigate('/admin-creation');
                });
            } else {
                swal.fire({
                    title: 'Notice',
                    text: response.data.message || `Error ${isEditMode ? 'updating' : 'creating'} admin`,
                    icon: 'warning',
                    confirmButtonText: 'Ok'
                });
                setError(response.data.message || `Error ${isEditMode ? 'updating' : 'creating'} admin`);
            }
        } catch (err) {
            console.error('Error creating admin:', err);
            swal.fire('Error', 'Internal server error', 'error');
            setError('Internal server error');
        }
    };

    return (
        <div className="admin-creation-wrapper">
            <div className="admin-card">
                <div className="admin-header" style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '15px' }}>
                    <button 
                        onClick={() => navigate('/admin-creation')} 
                        className="admin-btn" 
                        style={{ background: '#e2e8f0', color: '#4a5568', padding: '8px 15px', display: 'flex', alignItems: 'center', gap: '5px' }}
                    >
                        <i className="fa fa-arrow-left"></i> Back
                    </button>
                    <div>
                        <h2 style={{ margin: 0 }}>{isEditMode ? 'Update Administrator Details' : 'Administrator Details'}</h2>
                        <p style={{ margin: '5px 0 0 0' }}>{isEditMode ? 'Update the information below' : 'Fill in the required information to create a new admin account'}</p>
                    </div>
                </div>

                {error && <div style={{ color: 'red', marginBottom: '15px' }}>{error}</div>}

                <form onSubmit={handleSubmit} autoComplete="off" style={{ marginTop: '30px' }}>
                    <div className="admin-form">
                        <div className="form-group">
                            <label>First Name *</label>
                            <input 
                                type="text" 
                                name="firstName" 
                                placeholder="Enter first name" 
                                value={formData.firstName}
                                onChange={handleChange}
                                required 
                            />
                        </div>
                        <div className="form-group">
                            <label>Last Name *</label>
                            <input 
                                type="text" 
                                name="lastName" 
                                placeholder="Enter last name" 
                                value={formData.lastName}
                                onChange={handleChange}
                                required 
                            />
                        </div>

                        <div className="form-group">
                            <label>Phone Number</label>
                            <input 
                                type="text" 
                                name="mobile" 
                                placeholder="Enter 10 digit number"
                                value={formData.mobile}
                                onChange={handleChange}
                                maxLength="10"
                                minLength="10"
                                required 
                            />
                            
                        </div>
                        <div className="form-group">
                            <label>Admin Role *</label>
                            <select 
                                name="userType" 
                                value={formData.userType}
                                onChange={handleChange}
                                required
                            >
                                <option value="" disabled>Select admin role</option>
                                {roles.map(role => (
                                    <option key={role._id} value={role.roleName}>{role.roleName}</option>
                                ))}
                            </select>
                        </div>

                        {!isEditMode && (
                            <>
                                <div className="form-group">
                                    <label>Password *</label>
                                    <input 
                                        type={showPassword ? "text" : "password"} 
                                        name="password" 
                                        placeholder="••••••" 
                                        autoComplete="new-password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        minLength="6"
                                        required 
                                    />

                                </div>
                                <div className="form-group">
                                    <label>Confirm Password *</label>
                                    <input 
                                        type={showPassword ? "text" : "password"} 
                                        name="confirmPassword" 
                                        placeholder="Confirm password" 
                                        value={formData.confirmPassword}
                                        onChange={handleChange}
                                        required 
                                    />
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
                                        <input 
                                            type="checkbox" 
                                            id="showPassword" 
                                            checked={showPassword}
                                            onChange={(e) => setShowPassword(e.target.checked)}
                                            style={{ width: 'auto', margin: 0 }}
                                        />
                                        <label htmlFor="showPassword" style={{ margin: 0, cursor: 'pointer', fontSize: '13px', color: '#4a5568', textTransform: 'none' }}>Show Passwords</label>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px' }}>
                        <button type="submit" className="admin-btn admin-btn-primary">
                            {isEditMode ? 'Update Account' : 'Create Account'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddAdmin;
